/**
 * Live Trading V3 — the Attack (W/E) arm control.
 *
 * Sits beside the one-click arm as its aggressive sibling and follows the
 * same visibility rules: the effective attack size is visible at the
 * interaction surface, arming is unmistakable, and one click disarms.
 * Differences that carry meaning:
 *
 *   • Attack can only arm ON TOP of an armed one-click — the store refuses
 *     otherwise and the big red disarm kills both. The control renders
 *     disabled while one-click is disarmed so the dependency is visible,
 *     not discovered.
 *   • Amber, not red: a second, distinct danger color so "quoting hot" and
 *     "attacking hot" never read as one state.
 *   • The depth chip is read-only at 1. Depth above 1 is an unaccepted
 *     product semantic; the relay refuses it regardless, and a disabled
 *     control that SHOWS the bound teaches it better than a hidden one.
 *   • The title says what W/E do and that they may intentionally TAKE —
 *     taker economics are the point of the feature, not a surprise.
 *   • When a granted Attack action holds no key — the grant-arrival rule
 *     refusing to take back a default the trader reassigned — the title says
 *     so and names Settings (#740). It explains the state; it never resolves
 *     it, because auto-binding is the exact silent key theft that rule exists
 *     to prevent.
 *
 * The shared ack strip (OneClickArmControl) carries attack acks too — one
 * strip, one truth, no second place to watch.
 */

import { useEffect } from 'react';
import { Swords } from 'lucide-react';
import { cn } from '@/lib/infra/utils';
import { useOneClickStore } from '@/stores/oneClickStore';
import {
  useAttackStore,
  ATTACK_MAX_CONTRACTS,
  ATTACK_FALLBACK_MAX_CONTRACTS,
  ATTACK_DEPTH_CONTRACT,
} from '@/stores/attackStore';
import { useAttackAccess } from '@/hooks/useAttackAccess';
import { useAuthStore } from '@/stores/authStore';
import { useHotkeyLabel, useHotkeyUnbound } from '@/hooks/useLiveHotkeys';

export function AttackArmControl() {
  const attackAccess = useAttackAccess();
  // Per-trader server ceiling (traderRules.attackMaxContracts). Presentation
  // clamp only — the relay enforces regardless, and refuses outright when no
  // explicit ceiling exists.
  const serverCeiling = useAuthStore((s) => s.user?.attackMaxContracts ?? null);
  const effectiveMax =
    serverCeiling != null
      ? Math.min(ATTACK_MAX_CONTRACTS, serverCeiling)
      : ATTACK_FALLBACK_MAX_CONTRACTS;
  const oneClickArmed = useOneClickStore((s) => s.armed);
  const attackArmed = useAttackStore((s) => s.attackArmed);
  const attackSizeContracts = useAttackStore((s) => s.attackSizeContracts);
  // #725 — the tooltip teaches the trader's own Attack keys, not W/E.
  const attackTopKey = useHotkeyLabel('attack-top');
  const attackBottomKey = useHotkeyLabel('attack-bottom');
  const attackTopUnbound = useHotkeyUnbound('attack-top');
  const attackBottomUnbound = useHotkeyUnbound('attack-bottom');
  const armAttack = useAttackStore((s) => s.armAttack);
  const disarmAttack = useAttackStore((s) => s.disarmAttack);
  const setAttackSizeContracts = useAttackStore((s) => s.setAttackSizeContracts);

  // #740 — an Attack action the trader HOLDS can still arrive with no key:
  // the grant-arrival rule refuses to take back a factory key the trader had
  // already assigned to something else. All the tooltip could say about that
  // was a bare em dash, which is safe but leaves the trader with nowhere to
  // go. APPENDED, never woven in: the shipped sentence — the taker-liquidity
  // warning included — stays character-for-character what a trader with bound
  // keys already reads, and the hint exists only in the state it describes.
  // `useHotkeyUnbound` answers false for an ungranted or dormant action, so
  // this can never advertise a capability the trader does not hold.
  const unboundSides = [
    attackTopUnbound ? 'top' : null,
    attackBottomUnbound ? 'bottom' : null,
  ].filter((side): side is string => side != null);
  // Singular and plural are spelled out rather than joined, because "no key
  // is set for top or bottom — assign one" understates a state needing TWO
  // assignments, and reads as uncertainty about which side is affected.
  const unboundHint =
    unboundSides.length === 0
      ? ''
      : unboundSides.length === 1
        ? ` No key is set for Attack ${unboundSides[0]} — assign one in` +
          ' Settings → Trading defaults → Live Trading: key mapping.'
        : ' No keys are set for Attack top and bottom — assign them in' +
          ' Settings → Trading defaults → Live Trading: key mapping.';

  // Revocation converges the local state to safe, same as the one-click
  // control. The relay gate remains authoritative for anything in flight.
  useEffect(() => {
    if (!attackAccess) disarmAttack();
  }, [attackAccess, disarmAttack]);

  useEffect(() => {
    if (attackSizeContracts > effectiveMax) setAttackSizeContracts(effectiveMax);
  }, [effectiveMax, setAttackSizeContracts, attackSizeContracts]);

  if (!attackAccess) return null;

  return (
    <div className="flex min-w-0 items-center gap-2">
      <button
        type="button"
        data-lt2-hotkey-control
        disabled={!oneClickArmed}
        onClick={attackArmed ? disarmAttack : armAttack}
        title={
          attackArmed
            ? `Attack ARMED — ${attackTopKey} (top) / ${attackBottomKey} (bottom) submit one bounded limit at the level above the displayed bid, at the attack size. May intentionally TAKE liquidity (taker fees possible). Click to disarm.${unboundHint}`
            : oneClickArmed
              ? `Arm Attack: ${attackTopKey} (top) / ${attackBottomKey} (bottom) take-or-establish the level above the displayed bid at the attack size shown. May intentionally TAKE liquidity (taker fees possible). Session-only.${unboundHint}`
              : 'Attack arms only while one-click is armed.'
        }
        className={cn(
          'flex shrink-0 items-center gap-1.5 rounded-lg border px-2.5 py-1 text-xs font-semibold transition-colors',
          attackArmed
            ? 'border-amber-500 bg-amber-500/90 text-black'
            : oneClickArmed
              ? 'border-border/50 bg-muted/50 text-muted-foreground hover:text-foreground'
              : 'border-border/40 bg-muted/30 text-muted-foreground/50',
        )}
      >
        {attackArmed && (
          <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-black" aria-hidden />
        )}
        <Swords size={13} aria-hidden />
        {attackArmed ? 'ATTACK ARMED' : 'Attack'}
      </button>
      <label
        className={cn(
          'flex shrink-0 items-center gap-1 font-mono text-xs',
          attackArmed ? 'text-foreground' : 'text-muted-foreground',
        )}
        title={`Attack order size (contracts, max ${effectiveMax}) — separate from the one-click size`}
      >
        <span>×</span>
        <input
          type="number"
          min={1}
          max={effectiveMax}
          value={attackSizeContracts}
          onChange={(e) =>
            setAttackSizeContracts(Math.min(effectiveMax, parseInt(e.target.value, 10)))
          }
          className="border-border/50 bg-background w-14 rounded border px-1.5 py-0.5 text-right"
        />
      </label>
      <span
        className="text-muted-foreground shrink-0 rounded border border-border/50 bg-muted/40 px-1.5 py-0.5 font-mono text-[10px]"
        title={`Attack depth is fixed at ${ATTACK_DEPTH_CONTRACT} level in this release — the level directly above the displayed bid. Deeper semantics need explicit acceptance and are refused server-side.`}
      >
        {ATTACK_DEPTH_CONTRACT} lvl
      </span>
    </div>
  );
}
